/**
 * Modal
 * 注册 portal 形式的弹窗
 *
 *
 * Created by Samuel on 2022/8/15
 */
import { createContext, useCallback, useContext, useEffect, useReducer } from 'react';

import useEvent from '@core/hooks/useEvent';

const initialState = {};
const ModalContext = createContext(initialState);

const REGISTRY_MODALS = new Map();
const MOUNTED = {};

// 数据处理逻辑
let dispatch = () => {
	console.warn('No dispatch method detected. Please check Modal.Provider embed.');
};

const Action = {
	show: 'modal/show',
	hide: 'modal/hide',
	remove: 'modal/remove',
};
const _reducer = (state = initialState, action) => {
	switch (action.type) {
		case Action.show: {
			const { id, props } = action.payload;
			return {
				...state,
				[id]: {
					...state[id],
					id,
					props,
					visible: !!MOUNTED[id], // 没有初始化的，进行初始化操作，以避免进入动画无法生效
					delayVisible: !MOUNTED[id], // 标记是否需要进行延时显示
				},
			};
		}
		case Action.hide: {
			const { id } = action.payload;
			if (!state[id]) {
				return state;
			}
			return {
				...state,
				[id]: {
					...state[id],
					visible: false,
				},
			};
		}
		case Action.remove: {
			const { id } = action.payload;
			if (!state[id]) {
				return state;
			}
			let newState = { ...state };
			delete newState[id];
			return newState;
		}
		default: {
			return state;
		}
	}
};
const _show = (id, props) => {
	return {
		type: Action.show,
		payload: {
			id,
			props,
		},
	};
};
const _hide = (id) => {
	return {
		type: Action.hide,
		payload: {
			id,
		},
	};
};
const _remove = (id) => {
	return {
		type: Action.remove,
		payload: {
			id,
		},
	};
};

// 弹窗展示相关逻辑
let id = 0;
const getUid = () => `mg_modal_${id++}`;
// 唯一标识
const symModalId = Symbol('$$MG_ModalId$$');

const modalCallbacks = {};
const hideModalCallbacks = {};

// 获取弹窗的id or 动态生成一个id - 用于标识弹窗，一个弹窗只会生成一次ID
const getModalId = (modal) => {
	if (typeof modal === 'string') {
		return modal;
	}
	if (!modal[symModalId]) {
		modal[symModalId] = getUid();
	}

	return modal[symModalId];
};

const show = (modal, props) => {
	const id = getModalId(modal);
	if (typeof modal !== 'string' && !REGISTRY_MODALS.has(modal)) {
		register(id, modal);
	}

	dispatch(_show(id, props));
	if (!modalCallbacks[id]) {
		let _resolve, _reject;
		const promise = new Promise((resolve, reject) => {
			_resolve = resolve;
			_reject = reject;
		});
		modalCallbacks[id] = {
			resolve: _resolve,
			reject: _reject,
			promise,
		};
	}
	return modalCallbacks[id].promise;
};
const hide = (modal) => {
	const id = getModalId(modal);
	dispatch(_hide(id));
	delete modalCallbacks[id];
	if (!hideModalCallbacks[id]) {
		let _resolve, _reject;
		const promise = new Promise((resolve, reject) => {
			_resolve = resolve;
			_reject = reject;
		});
		hideModalCallbacks[id] = {
			resolve: _resolve,
			reject: _reject,
			promise,
		};
	}
	return hideModalCallbacks[id].promise;
};
const remove = (id) => {
	dispatch(_remove(id));
	delete modalCallbacks[id];
	delete hideModalCallbacks[id];
};

const bindModal = (Cp) => {
	const _Modal = (props) => {
		const { id, onClose, visible, delayVisible, ..._props } = props;

		useEffect(() => {
			MOUNTED[id] = true;
			return () => {
				delete MOUNTED[id];
			};
		}, [id]);

		useEffect(() => {
			if (delayVisible) {
				show(id, { ..._props, onClose });
			}
		}, [delayVisible, props]);

		// 成功时调用
		const onResolve = useEvent((args) => {
			if (modalCallbacks[id]) {
				modalCallbacks[id].resolve(args);
			}
		});
		// 失败时调用
		const onReject = useEvent((args) => {
			if (modalCallbacks[id]) {
				modalCallbacks[id].reject(args);
			}
		});

		// 展示状态变化时调用
		const handleVisibleChange = useEvent((visible) => {
			// console.log('handleVisibleChange', visible);
			if (!visible) {
				hide(id);
			}
		});

		const onHide = useCallback(() => {
			hide(id);
		}, [id]);

		// 隐藏层成功时调用
		const handleClose = useEvent((args) => {
			onClose && onClose(args);
			hideModalCallbacks[id]?.resolve(args);
		});

		return (
			<Cp
				visible={visible}
				{..._props}
				resolve={onResolve}
				reject={onReject}
				hide={onHide}
				onVisibleChange={handleVisibleChange}
				onClose={handleClose}
			/>
		);
	};
	_Modal.displayName = `M(${Cp.displayName})`;
	return _Modal;
};
const register = (id, component, props) => {
	if (REGISTRY_MODALS.has(id)) {
		REGISTRY_MODALS.set(id, {
			...REGISTRY_MODALS.get(id),
			props,
		});
	} else {
		REGISTRY_MODALS.set(id, {
			component: bindModal(component),
			props,
		});
	}
};
const unregister = (id) => {
	REGISTRY_MODALS.delete(id);
};

const ModalRenderer = () => {
	const modals = useContext(ModalContext);
	const visibleModals = Object.keys(modals)
		.filter((modalId) => /*modals[modalId] &&*/ REGISTRY_MODALS.has(modalId))
		.map((id) => {
			const m = REGISTRY_MODALS.get(id);
			const { props, id: _id, ...modalInfo } = modals[id];
			return <m.component key={id} id={id} {...m.props} {...modalInfo} {...props} />;
		});

	return <>{visibleModals}</>;
};

const Provider = ({ children }) => {
	const [modals, _dispatch] = useReducer(_reducer, initialState);
	dispatch = _dispatch;

	return (
		<ModalContext.Provider value={modals}>
			{children}
			<ModalRenderer />
		</ModalContext.Provider>
	);
};

export const useModal = (modal, props) => {
	const modals = useContext(ModalContext);
	const modalId = getModalId(modal);
	const isUseComponent = modal && typeof modal !== 'string';

	if (!modal) {
		throw new Error('No modal id found');
	}

	useEffect(() => {
		if (isUseComponent && !REGISTRY_MODALS.has(modalId)) {
			register(modalId, modal, props);
		}
	}, [isUseComponent, modal, props]);

	const modalInfo = modals[modalId];

	const showCallback = useCallback(
		(props) => {
			return show(modalId, props);
		},
		[modalId],
	);
	const hideCallback = useCallback(() => {
		return hide(modalId);
	}, [modalId]);
	const removeCallback = useCallback(() => {
		remove(modalId);
	}, [modalId]);
	const resolveCallback = useCallback(
		(args) => {
			modalCallbacks[modalId]?.resolve(args);
			delete modalCallbacks[modalId];
		},
		[modalId],
	);
	const rejectCallback = useCallback(
		(args) => {
			modalCallbacks[modalId]?.reject(args);
			delete modalCallbacks[modalId];
		},
		[modalId],
	);
	const resolveHideCallback = useCallback(
		(args) => {
			hideModalCallbacks[modalId]?.resolve(args);
			delete hideModalCallbacks[modalId];
		},
		[modalId],
	);

	return {
		id: modalId,
		props: modalInfo?.props,
		visible: !!modalInfo?.visible,
		keepMounted: !!modalInfo?.keepMounted,
		show: showCallback,
		hide: hideCallback,
		remove: removeCallback,
		resolve: resolveCallback,
		reject: rejectCallback,
		resolveHide: resolveHideCallback,
	};
};

const Modal = {
	Provider,
	register,
	unregister,
	show,
	hide,
	remove,
};
export default Modal;
